/**
 * Fetch wrapper with:
 *   - Authorization: Bearer <access> from useAuth store
 *   - Automatic single-flight refresh on 401
 *   - Offline outbox enqueue for write methods when navigator.onLine = false
 *   - Strongly-typed { ok, data, error } unwrapping
 */
import { useAuthStore } from '@/auth/store';
import { enqueue } from '@/lib/offline';

declare const __API_BASE__: string;

export type ApiSuccess<T> = { ok: true; data: T; meta?: ApiMeta };
export type ApiError = {
  ok: false;
  error: { code: string; message: string; fields?: Record<string, string> };
};
export type ApiMeta = { total: number; page: number; limit: number };
export type ApiResponse<T> = ApiSuccess<T> | ApiError;

export class ApiException extends Error {
  status: number;
  code: string;
  fields?: Record<string, string>;

  constructor(status: number, code: string, message: string, fields?: Record<string, string>) {
    super(message);
    this.name = 'ApiException';
    this.status = status;
    this.code = code;
    if (fields) this.fields = fields;
  }
}

/**
 * Anything we can flatten into a URL query string. We accept any plain
 * object so callers can pass typed `*Query` interfaces directly without
 * needing to add an index signature to each one.
 */
export type QueryParams = object;

export interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: QueryParams;
  /** if true, requests that fail due to offline are queued for retry */
  offlineQueue?: boolean;
  signal?: AbortSignal;
}

let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${__API_BASE__}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return null;
      const body = (await res.json()) as ApiResponse<{
        access: string;
        user?: { userid: number; username: string; memberid?: number };
        config?: { proforma_valid_days: number };
      }>;
      if (!body.ok) return null;
      const state = useAuthStore.getState();
      const user = body.data.user && state.user
        ? { ...state.user, ...body.data.user, memberid: body.data.user.memberid ?? state.user.memberid }
        : state.user;
      if (user) {
        state.setSession(body.data.access, user, body.data.config ?? state.config);
      } else {
        state.setAccess(body.data.access);
      }
      return body.data.access;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export async function api<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const isWrite = method !== 'GET';
  const url = `${__API_BASE__}${path}${buildQuery(opts.query)}`;

  if (isWrite && !navigator.onLine && opts.offlineQueue !== false) {
    await enqueue({ url, method, body: opts.body });
    return { __queued: true } as T;
  }

  const auth = useAuthStore.getState();
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth.access) headers['Authorization'] = `Bearer ${auth.access}`;

  let res = await fetch(url, {
    method,
    credentials: 'include',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  // Access token is memory-only; retry refresh when session exists (user persisted).
  if (res.status === 401 && (auth.access || auth.user)) {
    const fresh = await refreshAccessToken();
    if (fresh) {
      headers['Authorization'] = `Bearer ${fresh}`;
      res = await fetch(url, {
        method,
        credentials: 'include',
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: opts.signal,
      });
    } else {
      useAuthStore.getState().logout();
    }
  }

  if (res.status === 204) return undefined as T;

  let payload: ApiResponse<T> | null = null;
  try {
    payload = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiException(res.status, 'bad_response', `Bad response (${res.status})`);
  }

  if (!payload || !payload.ok) {
    const err = payload && !payload.ok ? payload.error : null;
    throw new ApiException(
      res.status,
      err?.code ?? 'unknown',
      err?.message ?? `Request failed (${res.status})`,
      err?.fields,
    );
  }

  return payload.data;
}

/** GET that also returns pagination meta. */
export async function apiList<T = unknown>(
  path: string,
  query?: QueryParams,
): Promise<{ data: T[]; meta?: ApiMeta }> {
  const url = `${__API_BASE__}${path}${buildQuery(query)}`;
  const auth = useAuthStore.getState();
  const headers: Record<string, string> = {};
  if (auth.access) headers['Authorization'] = `Bearer ${auth.access}`;

  let res = await fetch(url, { credentials: 'include', headers });
  if (res.status === 401 && (auth.access || auth.user)) {
    const fresh = await refreshAccessToken();
    if (fresh) {
      headers['Authorization'] = `Bearer ${fresh}`;
      res = await fetch(url, { credentials: 'include', headers });
    } else {
      useAuthStore.getState().logout();
    }
  }
  const body = (await res.json()) as ApiResponse<T[]>;
  if (!body.ok) {
    throw new ApiException(res.status, body.error.code, body.error.message, body.error.fields);
  }
  return { data: body.data, ...(body.meta ? { meta: body.meta } : {}) };
}
